import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, ArrowUpRight } from 'lucide-react';
import AnimateOnScroll from './AnimateOnScroll';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus({ type: 'success', message: t(translations.contact.success) });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error sending message:', error);
      }
      setStatus({ type: 'error', message: t(translations.contact.error) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-white">
      {/* CTA Section */}
      <div className="py-32 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimateOnScroll animation="fade-up">
            <div className="max-w-4xl">
              <p className="text-neutral-500 text-sm tracking-[0.3em] uppercase mb-6">
                {t({ fr: 'Contactez-nous', en: 'Contact us' })}
              </p>
              <h2 className="text-5xl md:text-7xl lg:text-8xl font-light text-neutral-900 leading-[1.1] mb-8">
                {t({ fr: 'Construisons', en: "Let's Build" })}
                <br />
                <span className="text-neutral-400">{t({ fr: 'Ensemble', en: 'Together' })}</span>
              </h2>
              <p className="text-neutral-500 text-xl max-w-2xl">
                {t({ 
                  fr: 'Prêt à concrétiser votre projet ? Notre équipe est à votre disposition pour discuter de vos besoins.',
                  en: 'Ready to bring your project to life? Our team is available to discuss your needs.'
                })}
              </p>
            </div>
          </AnimateOnScroll>
        </div>
      </div>

      {/* Contact Form & Info */}
      <div className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20">
            {/* Contact Info */}
            <AnimateOnScroll animation="fade-right">
              <div className="space-y-12">
                <div>
                  <p className="text-neutral-400 text-sm tracking-[0.2em] uppercase mb-6">
                    {t({ fr: 'Informations', en: 'Information' })}
                  </p>
                  
                  <div className="space-y-8">
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=Rue+du+Centre+72,+1025+St-Sulpice,+Suisse"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start space-x-4 group"
                    >
                      <MapPin className="w-5 h-5 text-neutral-500 mt-1" />
                      <div>
                        <p className="text-neutral-800 group-hover:text-neutral-500 transition-colors">Rue du Centre 72</p>
                        <p className="text-neutral-500">1025 St-Sulpice, Suisse</p>
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-neutral-300 group-hover:text-neutral-500 transition-colors" />
                    </a>

                    <a href="mailto:contact@myihome.ch" className="flex items-center space-x-4 group">
                      <Mail className="w-5 h-5 text-neutral-500" />
                      <span className="text-neutral-800 group-hover:text-neutral-500 transition-colors">contact@myihome.ch</span>
                    </a>

                    <a href="tel:+41219032859" className="flex items-center space-x-4 group">
                      <Phone className="w-5 h-5 text-neutral-500" />
                      <span className="text-neutral-800 group-hover:text-neutral-500 transition-colors">+41 21 903 28 59</span>
                    </a>
                  </div>
                </div>

                <div className="pt-8 border-t border-neutral-200">
                  <p className="text-neutral-400 text-sm mb-4">
                    {t({ fr: 'Votre message sera envoyé à', en: 'Your message will be sent to' })}
                  </p>
                  <p className="text-neutral-600 text-lg">contact@myihome.ch</p>
                </div>
              </div>
            </AnimateOnScroll>

            {/* Form */}
            <AnimateOnScroll animation="fade-left" delay={1}>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-neutral-400 text-sm mb-2">{t(translations.contact.name)} *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-neutral-300 py-3 text-neutral-800 focus:border-neutral-500 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-neutral-400 text-sm mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-neutral-300 py-3 text-neutral-800 focus:border-neutral-500 focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-neutral-400 text-sm mb-2">{t(translations.contact.phone)}</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-neutral-300 py-3 text-neutral-800 focus:border-neutral-500 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-neutral-400 text-sm mb-2">{t(translations.contact.subject)} *</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-neutral-300 py-3 text-neutral-800 focus:border-neutral-500 focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-neutral-400 text-sm mb-2">{t(translations.contact.message)} *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full bg-transparent border-b border-neutral-300 py-3 text-neutral-800 focus:border-neutral-500 focus:outline-none transition-colors resize-none"
                  ></textarea>
                </div>

                {status.message && (
                  <div className={`p-4 ${status.type === 'success' ? 'bg-neutral-100 text-neutral-600' : 'bg-red-50 text-red-600'}`}>
                    {status.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center space-x-3 bg-neutral-500 text-white px-8 py-4 hover:bg-neutral-600 transition-all disabled:opacity-50"
                  data-testid="contact-submit-button"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span>{t(translations.contact.send)}</span>
                      <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>
            </AnimateOnScroll>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_archspace-12/artifacts/kmu49d1s_myiHome_Masters-logo_web.png" 
              alt="my iHome" 
              className="h-8 w-auto mb-4 md:mb-0"
            />
            <p className="text-neutral-400 text-sm">
              © {new Date().getFullYear()} my iHome SA. {t({ fr: 'Tous droits réservés.', en: 'All rights reserved.' })}
            </p>
          </div>
        </div>
      </footer>
    </section>
  );
};

export default Contact;
